// src/logistique/vehicules/VehiculeAjouter.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Row, Col, Form, Button, Spinner, InputGroup, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   SELECT AVEC RECHERCHE
   ============================================================ */
const SearchableSelect = ({
    options = [], value, onChange,
    placeholder = "Sélectionner...", searchPlaceholder = "Rechercher...",
    labelKey = "label", valueKey = "value",
    emptyMessage = "Aucun résultat", icon = "ri-search-line",
    required = false, disabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false); setSearch('');
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        const s = search.toLowerCase();
        return options.filter(o => String(o[labelKey] || '').toLowerCase().includes(s));
    }, [options, search, labelKey]);

    const selected = options.find(o => String(o[valueKey]) === String(value));

    return (
        <div className="searchable-select" ref={wrapperRef}>
            <div
                className={`searchable-select-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setOpen(o => !o)}
                tabIndex={0}
            >
                <i className={`${icon} me-2`}></i>
                <span className={selected ? '' : 'text-muted'}>
                    {selected ? selected[labelKey] : placeholder}
                </span>
                <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line ms-auto`}></i>
            </div>

            {open && (
                <div className="searchable-select-menu">
                    <div className="searchable-select-search">
                        <i className="ri-search-line"></i>
                        <input
                            type="text" autoFocus
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="searchable-select-options">
                        {filtered.length === 0 ? (
                            <div className="searchable-select-empty">{emptyMessage}</div>
                        ) : (
                            filtered.map((opt, idx) => (
                                <div
                                    key={`${opt[valueKey]}-${idx}`}
                                    className={`searchable-select-option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                    onClick={() => { onChange(opt[valueKey]); setOpen(false); setSearch(''); }}
                                >
                                    {opt[labelKey]}
                                    {String(opt[valueKey]) === String(value) && (
                                        <i className="ri-check-line ms-auto" style={{ color: COLORS.primary }}></i>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            {required && (
                <input type="text" value={value || ''} required onChange={() => {}} tabIndex={-1}
                    style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            )}
        </div>
    );
};

/* ============================================================
   PAGE
   ============================================================ */
export default function VehiculeAjouter() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [services, setServices] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Modal création rapide de service
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [savingService, setSavingService] = useState(false);
    const [serviceError, setServiceError] = useState('');
    const [nouveauService, setNouveauService] = useState({ nom_service: '', description: '' });

    // 🔔 Toast
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const [vehicule, setVehicule] = useState({
        immatriculation: '', marque: '', modele: '', type_vehicule: 'interne',
        prestataire_externe: '', id_service_proprietaire: '', statut: 'disponible',
        date_acquisition: '', date_fin_contrat: '', kilometrage_actuel: 0
    });

    useEffect(() => {
        fetchServices();
        if (isEdit) fetchVehicule();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchServices = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/services.php?action=list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setServices(result.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchVehicule = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/vehicules.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            if (result.data) {
                setVehicule({
                    ...result.data,
                    id_service_proprietaire: result.data.id_service_proprietaire || ''
                });
            }
        } catch (err) { console.error(err); }
    };

    /* ============================================================
       OPTIONS
       ============================================================ */
    const serviceOptions = useMemo(() =>
        services.map(s => ({
            value: s.id_service,
            label: s.nom_service
        })), [services]
    );

    /* ============================================================
       CRÉATION RAPIDE DE SERVICE — avec toast
       ============================================================ */
    const handleCreateService = async (e) => {
        e.preventDefault();
        if (!nouveauService.nom_service.trim()) {
            setServiceError("Le nom du service est obligatoire");
            return;
        }
        setSavingService(true);
        setServiceError('');
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/services.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nom_service: nouveauService.nom_service.trim(),
                    description: nouveauService.description.trim() || null
                })
            });
            const result = await res.json();
            if (result.success) {
                await fetchServices();
                setVehicule(prev => ({ ...prev, id_service_proprietaire: result.id }));
                setShowServiceModal(false);
                setNouveauService({ nom_service: '', description: '' });
                showToast('success', 'Service créé',
                    `"${nouveauService.nom_service}" a été créé et sélectionné.`);
            } else {
                setServiceError(result.message || "Erreur lors de la création");
                showToast('error', 'Erreur', result.message || "Impossible de créer le service");
            }
        } catch (err) {
            setServiceError(err.message);
            showToast('error', 'Erreur de connexion', err.message);
        } finally {
            setSavingService(false);
        }
    };

    /* ============================================================
       SOUMISSION — avec toast
       ============================================================ */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!vehicule.immatriculation.trim()) {
            setError("L'immatriculation est obligatoire");
            showToast('warning', 'Champ requis', "L'immatriculation est obligatoire");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");

            const payload = {
                ...vehicule,
                id_service_proprietaire: vehicule.type_vehicule === 'externe'
                    ? null
                    : (vehicule.id_service_proprietaire || null)
            };

            const method = isEdit ? 'PUT' : 'POST';
            const url = isEdit
                ? `${API_BASE_URL}/vehicules.php?id=${id}`
                : `${API_BASE_URL}/vehicules.php`;
            const body = isEdit ? payload : { action: 'create_vehicule', ...payload };

            const res = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const result = await res.json();

            if (result.success) {
                showToast('success',
                    isEdit ? 'Véhicule modifié' : 'Véhicule créé',
                    isEdit
                        ? `Le véhicule ${vehicule.immatriculation} a été mis à jour.`
                        : `Le véhicule ${vehicule.immatriculation} a été enregistré.`
                );
                setTimeout(() => navigate('/logistique/vehicules'), 1800);
            } else {
                setError(result.message || "Erreur");
                showToast('error', 'Erreur', result.message || "Impossible d'enregistrer le véhicule");
            }
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur de connexion', err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* 🔔 TOAST */}
                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className={isEdit ? "ri-pencil-line" : "ri-add-line"}></i>
                            </div>
                            <div><h5 className="header-title">{isEdit ? 'Modifier' : 'Nouveau'} véhicule</h5></div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate('/logistique/vehicules')}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {/* Alerte inline pour erreurs de validation */}
                    {error && <div className="alert alert-danger mb-4">{error}</div>}

                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-roadster-line me-2"></i>
                                        Informations du véhicule
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Immatriculation <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-roadster-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            value={vehicule.immatriculation}
                                                            onChange={(e) => setVehicule({ ...vehicule, immatriculation: e.target.value.toUpperCase() })}
                                                            placeholder="Ex: GA-1234-AB"
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Type <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            value={vehicule.type_vehicule}
                                                            onChange={(e) => setVehicule({ ...vehicule, type_vehicule: e.target.value })}
                                                        >
                                                            <option value="interne">Interne</option>
                                                            <option value="externe">Externe</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Marque</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-price-tag-3-line"></i></InputGroup.Text>
                                                        <Form.Control value={vehicule.marque || ''} onChange={(e) => setVehicule({ ...vehicule, marque: e.target.value })} />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Modèle</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-price-tag-3-line"></i></InputGroup.Text>
                                                        <Form.Control value={vehicule.modele || ''} onChange={(e) => setVehicule({ ...vehicule, modele: e.target.value })} />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {vehicule.type_vehicule === 'interne' && (
                                                <Col md={12}>
                                                    <Form.Group>
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <Form.Label className="form-label-modern mb-0">Service propriétaire</Form.Label>
                                                            <button type="button" className="btn-link-create" onClick={() => setShowServiceModal(true)}>
                                                                <i className="ri-add-circle-line me-1"></i> Créer un service
                                                            </button>
                                                        </div>
                                                        <SearchableSelect
                                                            options={serviceOptions}
                                                            value={vehicule.id_service_proprietaire}
                                                            onChange={(v) => setVehicule({ ...vehicule, id_service_proprietaire: v })}
                                                            placeholder="Sélectionner un service (optionnel)"
                                                            searchPlaceholder="Rechercher un service..."
                                                            emptyMessage="Aucun service trouvé"
                                                            icon="ri-home-office-line"
                                                        />
                                                        {services.length === 0 && (
                                                            <Form.Text className="text-muted small">
                                                                Aucun service disponible. Cliquez sur « Créer un service ».
                                                            </Form.Text>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                            )}

                                            {vehicule.type_vehicule === 'externe' && (
                                                <>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Prestataire</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                                <Form.Control value={vehicule.prestataire_externe || ''} onChange={(e) => setVehicule({ ...vehicule, prestataire_externe: e.target.value })} placeholder="Nom du loueur" />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Date fin de contrat</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                                <Form.Control type="date" value={vehicule.date_fin_contrat || ''} onChange={(e) => setVehicule({ ...vehicule, date_fin_contrat: e.target.value })} />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                </>
                                            )}

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Statut</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-information-line"></i></InputGroup.Text>
                                                        <Form.Select value={vehicule.statut} onChange={(e) => setVehicule({ ...vehicule, statut: e.target.value })}>
                                                            <option value="disponible">Disponible</option>
                                                            <option value="en_mission">En mission</option>
                                                            <option value="en_maintenance">Maintenance</option>
                                                            <option value="hors_service">Hors service</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Kilométrage</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-dashboard-line"></i></InputGroup.Text>
                                                        <Form.Control type="number" min="0" value={vehicule.kilometrage_actuel} onChange={(e) => setVehicule({ ...vehicule, kilometrage_actuel: parseInt(e.target.value) || 0 })} />
                                                        <InputGroup.Text className="bg-light">km</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date d'acquisition</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control type="date" value={vehicule.date_acquisition || ''} onChange={(e) => setVehicule({ ...vehicule, date_acquisition: e.target.value })} />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button type="button" className="btn-modern-outline" onClick={() => navigate('/logistique/vehicules')}>
                                                        Annuler
                                                    </Button>
                                                    <Button type="submit" className="btn-modern-primary" disabled={saving}>
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                        ) : (
                                                            <><i className="ri-save-line me-2"></i>{isEdit ? 'Modifier' : 'Créer'}</>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL NOUVEAU SERVICE ===== */}
            <Modal show={showServiceModal} onHide={() => !savingService && setShowServiceModal(false)} centered backdrop={savingService ? 'static' : true}>
                <Modal.Header closeButton={!savingService} className="card-header-gradient">
                    <Modal.Title className="text-white">
                        <i className="ri-add-circle-line me-2"></i>
                        Créer un nouveau service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateService}>
                    <Modal.Body>
                        {serviceError && <div className="alert alert-danger py-2 px-3 small mb-3">{serviceError}</div>}
                        <p className="text-muted small mb-3">
                            Le service sera créé et sélectionné automatiquement comme propriétaire du véhicule.
                        </p>
                        <Row className="g-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Nom du service <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        value={nouveauService.nom_service}
                                        onChange={(e) => setNouveauService({ ...nouveauService, nom_service: e.target.value })}
                                        placeholder="Ex: Logistique, Maintenance, Direction..."
                                        autoFocus required
                                        disabled={savingService}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Description</Form.Label>
                                    <Form.Control
                                        as="textarea" rows={2}
                                        value={nouveauService.description}
                                        onChange={(e) => setNouveauService({ ...nouveauService, description: e.target.value })}
                                        placeholder="Description du service (optionnel)"
                                        disabled={savingService}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="secondary" onClick={() => setShowServiceModal(false)} disabled={savingService}>
                            Annuler
                        </Button>
                        <Button type="submit" className="btn-modern-primary" disabled={savingService}>
                            {savingService ? (
                                <><Spinner size="sm" className="me-2" />Création...</>
                            ) : (
                                <><i className="ri-save-line me-2"></i>Créer et sélectionner</>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== STYLES ===== */}
            <style>{`
                .header-modern { background: white; padding: 1.2rem 1.8rem; border-left: 4px solid ${COLORS.secondary}; border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary}); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .card-header-gradient { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; }
                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; }
                .input-modern .input-group-text { background: #f8fafc; border: 2px solid #e2e8f0; border-right: none; color: ${COLORS.primary}; border-radius: 10px 0 0 10px; }
                .input-modern .form-control, .input-modern .form-select { border: 2px solid #e2e8f0; border-left: none; border-radius: 0 10px 10px 0; padding: 0.6rem 0.9rem; }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; color: #4b5563; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary:disabled { opacity: 0.7; cursor: not-allowed; }
                .btn-link-create { background: transparent; border: none; color: ${COLORS.secondary}; font-size: 0.8rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; padding: 0; }
                .btn-link-create:hover { color: ${COLORS.primary}; text-decoration: underline; }

                /* ===== SEARCHABLE SELECT ===== */
                .searchable-select { position: relative; }
                .searchable-select-trigger { display: flex; align-items: center; padding: 0.6rem 0.9rem; border: 2px solid #e2e8f0; border-radius: 10px; background: white; cursor: pointer; font-size: 0.9rem; }
                .searchable-select-trigger.open { border-color: ${COLORS.secondary}; box-shadow: 0 0 0 3px rgba(33,172,205,0.12); }
                .searchable-select-trigger i:first-child { color: ${COLORS.primary}; }
                .searchable-select-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: white; border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.15); z-index: 1000; overflow: hidden; }
                .searchable-select-search { display: flex; align-items: center; gap: 8px; padding: 0.7rem 1rem; border-bottom: 1px solid #f1f5f9; }
                .searchable-select-search i { color: #94a3b8; }
                .searchable-select-search input { border: none; outline: none; flex: 1; font-size: 0.9rem; }
                .searchable-select-options { max-height: 260px; overflow-y: auto; padding: 0.4rem; }
                .searchable-select-option { padding: 0.55rem 0.8rem; border-radius: 8px; cursor: pointer; font-size: 0.88rem; display: flex; align-items: center; }
                .searchable-select-option:hover { background: #f1f5f9; }
                .searchable-select-option.selected { background: ${COLORS.primary}10; color: ${COLORS.primary}; font-weight: 600; }
                .searchable-select-empty { padding: 1rem; text-align: center; color: #94a3b8; font-size: 0.85rem; }

                /* ===== TOAST ===== */
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-progress { height: 3px; animation: shrink linear forwards; }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
            `}</style>
        </>
    );
}